import { useState, useMemo, useContext, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Line, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart
} from 'recharts';
import {
  TrendingUp, Star, DollarSign, Calendar,
  Eye, Sparkles, Building2, Loader2, Target, RefreshCw,
  Info
} from 'lucide-react';
import FormulaReveal, { HOTEL_FORMULAS } from '@/components/ui/FormulaReveal';
import { PriceTooltip } from './ChartTooltips';
import { useDashboardFilters } from '@/contexts/DashboardFilters';
import { useAIInsight } from '@/hooks/useAIInsight';
import DemandCalendar from './DemandCalendar';
import { DashboardContext, useDashboard } from '@/contexts/DashboardContext';
import { useCompset } from '@/contexts/CompsetContext';
import { formatCurrency } from '@/helpers/formatters';
import { TooltipContent, TooltipProvider, TooltipTrigger, Tooltip as RadixToolTip } from '../ui/tooltip';
import { usePlans } from '@/contexts/PlanRulesContext';
import DashboardError from '../error/DashboardError';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/services/queryKeyFactory';
import AIRecommandationCard from '../cards/AIRecommandationCard';

export default function MarketInsights() {

  const [data, setData] = useState(null);
  const [chartTimeRange, setChartTimeRange] = useState('7d');
  const [activeTab, setActiveTab] = useState('market-overview');

  const { timeRangePreset, selectedCompetitors } = useDashboardFilters();
  const { activeCompset, competitors } = useCompset()

  const { marketInsightData, isLoadingMarketInsight, isErrorMarketInsight, isEnabledMarketInsight, allAiInsights } = useDashboard();
  const { canAccessAiInsights, calculateAiInsightRefreshCount, maxAiRefreshPerSection } = usePlans();

  const filteredCompetitors = useMemo(() => {
    if (!competitors) return [];
    if (selectedCompetitors.length === 0) return competitors;
    return competitors.filter(c => selectedCompetitors.includes(c.competitor_id || c.id));
  }, [competitors, selectedCompetitors]);

  const queryClient = useQueryClient();

  // Generate KPI data from API response
  const getKPIData = () => {
    const kpis = marketInsightData?.kpis || {};
    const hotelData = marketInsightData?.your_hotel || {};


    // Ensure rating is a number
    const rating = parseFloat(hotelData?.rating);

    return [
      {
        title: 'Overall Review',
        value: rating ? rating.toFixed(1) : "N/A",
        suffix: rating ? '/5' : null,
        change: '+0.2',
        trend: 'up',
        icon: Star,
        color: 'text-amber-500',
        gradient: 'from-amber-500 to-orange-500',
        bgGradient: 'from-amber-500/10 to-orange-500/10',
        formula: {
          title: 'Overall Review Rating',
          type: 'kpi',
          formula: 'Weighted average of ratings across all review platforms.',
          description: 'Weighted average of ratings across all review platforms.',
          variables: {
            'rating': { value: rating.toFixed(1), description: 'Current rating' },
            'total_reviews': { value: (hotelData.reviews || 432).toString(), description: 'Total reviews' }
          },
          methodology: `1. Collect ratings from each connected platform
2. Normalize ratings to 5-point scale
3. Apply weights based on review volume
4. Calculate weighted average`,
          dataSource: 'Review Aggregation System'
        }
      },
      {
        title: 'Lowest Price',
        value: kpis.lowest_price ? formatCurrency(kpis.lowest_price, activeCompset?.currency) : 'N/A',
        change: '-5.2%',
        trend: 'down',
        icon: DollarSign,
        color: 'text-emerald-500',
        gradient: 'from-emerald-500 to-teal-500',
        bgGradient: 'from-emerald-500/10 to-teal-500/10',
        formula: {
          ...HOTEL_FORMULAS.LOWEST_PRICE,
          variables: {
            'lowest_rate': { value: kpis.lowest_price ? formatCurrency(kpis.lowest_price, activeCompset?.currency) : 'N/A', description: 'Minimum rate across OTAs' },
            'market_avg': { value: kpis.market_average ? formatCurrency(kpis.market_average, activeCompset?.currency) : 'N/A', description: 'Market average' }
          }
        }
      },
      {
        title: 'Today Price',
        value: kpis.today_price ? formatCurrency(kpis.today_price, activeCompset?.currency) : 'N/A',
        change: '+2.1%',
        trend: 'up',
        icon: Calendar,
        color: 'text-blue-500',
        gradient: 'from-blue-500 to-indigo-500',
        bgGradient: 'from-blue-500/10 to-indigo-500/10',
        formula: {
          title: "Today's Best Available Rate",
          type: 'kpi',
          formula: 'Today_Price = Lowest_Rate_Across_All_OTAs_For_Today',
          description: 'The best available rate for today across all distribution channels.',
          variables: {
            'lowest_rate': { value: kpis.today_price ? formatCurrency(kpis.today_price, activeCompset?.currency) : 'N/A', description: 'Lowest available rate' }
          },
          methodology: `1. Query all OTA rates for today
2. Include direct booking rate
3. Return minimum rate`,
          dataSource: 'Real-time OTA Scraping'
        }
      },
      {
        title: 'Market Position',
        value: kpis?.market_position ? `#${kpis?.market_position}` : "N/A",
        suffix: kpis?.total_competitors ? ` of ${kpis?.total_competitors}` : "",
        change: 'Premium',
        trend: 'neutral',
        icon: Target,
        color: 'text-purple-500',
        gradient: 'from-purple-500 to-pink-500',
        bgGradient: 'from-purple-500/10 to-pink-500/10',
        formula: {
          ...HOTEL_FORMULAS.MARKET_POSITION,
          variables: {
            'your_position': { value: `#${kpis.market_position || 1}`, description: 'Your ranking' },
            'total_hotels': { value: (kpis.total_competitors || 6).toString(), description: 'Hotels in compset' }
          }
        }
      }
    ];
  };

  // Generate chart data from API response with fallback, respecting time range filter
  const getChartData = () => {
    const trends = marketInsightData?.price_trends || [];

    // Determine number of days based on chartTimeRange
    const daysToShow =
      chartTimeRange === '7d'
        ? 7
        : chartTimeRange === '14d'
          ? 14 : 30;

    // Filter trends by time range
    const filteredTrends = trends.slice(0, daysToShow);

    return filteredTrends.map(day => {
      const filteredDay = {
        date: day.date?.split('-').slice(1).join('/') || day.date,
        fullDate: day.date,
        yourHotel: day.yourHotel,
        marketAvg: day.marketAvg,
      };

      return filteredDay;
    });
  };

  // Generate room availability data from API with fallback
  const getRoomAvailability = () => {
    const roomData = marketInsightData?.room_availability || [];
    return roomData;
  };

  // AI Insight for market analysis - must be before any conditional returns
  const aiMarketData = useMemo(() => ({
    position: marketInsightData?.kpis?.market_position || "N/A",
    total_competitors: (competitors?.length) + 1,
    market_avg: marketInsightData?.kpis?.market_average,
    your_price: marketInsightData?.kpis?.lowest_price,
    // occupancy_trend: data?.kpis?.occupancy_change > 0 ? 'increasing' : 'stable'
  }), [marketInsightData, competitors]);

  const { insight: aiMarketInsight, loading: aiMarketLoading, refetch: refetchMarketAI, isAIGenerated: isMarketAIGenerated, isEnabled, enableAiInsight } = useAIInsight(
    'market',
    aiMarketData,
    (!isLoadingMarketInsight && isEnabledMarketInsight && !!marketInsightData && canAccessAiInsights())
  );



  // Generate chart data - must be before conditional returns
  const chartData = useMemo(() => {
    if (isLoadingMarketInsight) return [];
    return getChartData();
  }, [marketInsightData, chartTimeRange, selectedCompetitors, filteredCompetitors, isLoadingMarketInsight]);

  if (isLoadingMarketInsight) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading market insights...</p>
        </div>
      </div>
    );
  }

  if (isErrorMarketInsight) {
    return <DashboardError
      onRetry={() => queryClient.refetchQueries({ queryKey: queryKeys.marketInsights() })}
    />
  }

  const kpiData = getKPIData();
  const roomData = getRoomAvailability();


  const hasAiInsightData = allAiInsights?.insights?.find(insight => insight?.section === "market");


  const competitorsList = filteredCompetitors.length > 0 ? filteredCompetitors : marketInsightData?.competitors;

  const priceChartFormula = {
    title: 'Price Trend Analysis Chart',
    type: 'chart',
    formula: `For each date in range:
  Your_Hotel_Price = MIN(all OTA rates)
  Market_Avg = AVG(MIN(rates) for each hotel)`,
    description: 'Time series comparison of your lowest rates vs competitors and market average.',
    variables: {
      'time_range': { value: timeRangePreset?.toUpperCase(), description: 'Selected time period' },
      'data_points': { value: chartData?.length.toString(), description: 'Number of days plotted' },
      'competitors_shown': { value: competitorsList?.length?.toString(), description: 'Competitors displayed' }
    },
    methodology: `1. For each date, scrape rates from all OTAs
2. Calculate lowest rate per hotel
3. Plot your hotel's lowest rate as primary line
4. Calculate and plot market average
5. Filter based on selected competitors`,
    dataSource: 'OTA Scraping Engine'
  };

  const roomAvailabilityFormula = {
    title: 'Room Availability Analysis',
    type: 'chart',
    formula: `Availability_% = (Available_Rooms / Total_Rooms) × 100
Occupancy_% = 100 - Availability_%`,
    description: 'Current room availability across your compset.',
    methodology: `1. Query inventory from PMS
2. Estimate competitor availability from OTA signals
3. Calculate occupancy percentages`,
    dataSource: 'PMS + OTA Signals'
  };

  const aiInsightFormula = {
    ...HOTEL_FORMULAS.AI_INSIGHT_GENERATION,
    variables: {
      'your_avg_rate': { value: marketInsightData?.kpis?.market_average ? formatCurrency(marketInsightData?.kpis?.market_average, activeCompset?.currency) : 'N/A', description: 'Your average rate' },
      'market_position': { value: `#${marketInsightData?.kpis?.market_position || 1}`, description: 'Your market position' },
      'competitors': { value: (competitorsList?.length || 5).toString(), description: 'Hotels analyzed' }
    }
  };


  return (
    <div className="space-y-6 animate-in fade-in duration-500" data-testid="market-insights">
      {/* Tabs for Market Overview and Demand & Events */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-primary/5">
          <TabsTrigger value="market-overview" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-500 data-[state=active]:text-white">
            Market Overview
          </TabsTrigger>
          <TabsTrigger value="demand-events" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-500 data-[state=active]:text-white">
            Demand & Events
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Market Overview */}
        <TabsContent value="market-overview" className="space-y-6">
          {/* KPI Cards with Modern Gradients */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {kpiData?.map((kpi, index) => (
              <Card key={index} className="relative overflow-hidden border-2 border-primary/10 hover:border-primary/30 transition-all hover:shadow-xl group">
                {/* Gradient background overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${kpi.bgGradient} opacity-50`} />

                <CardContent className="relative p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${kpi.gradient} shadow-lg group-hover:scale-110 transition-transform`}>
                      <kpi.icon className="h-5 w-5 text-white" />
                    </div>
                    <FormulaReveal {...kpi.formula}>
                      <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/50">
                        <Info className="h-4 w-4 text-muted-foreground hover:text-primary" />
                      </Button>
                    </FormulaReveal>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1 font-medium">{kpi.title}</p>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-foreground">{kpi.value}</span>
                      {kpi.suffix && <span className="text-sm text-muted-foreground">{kpi.suffix}</span>}
                    </div>
                    {/* <div className="flex items-center gap-1 mt-3">
                      {kpi.trend === 'up' && <ArrowUpRight className="h-4 w-4 text-emerald-500" />}
                      {kpi.trend === 'down' && <ArrowDownRight className="h-4 w-4 text-red-500" />}
                      {kpi.trend === 'neutral' && <Target className="h-4 w-4 text-purple-500" />}
                      <span className={`text-sm font-medium ${kpi.trend === 'up' ? 'text-emerald-500' :
                        kpi.trend === 'down' ? 'text-red-500' :
                          'text-purple-500'
                        }`}>
                        {kpi.change}
                      </span>
                    </div> */}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Price Trend Chart */}
            <Card className="lg:col-span-2 border-2 border-primary/10 hover:shadow-xl transition-all">
              <CardHeader className="pb-3 sm:pb-6">

                {/* ── Desktop: single row (unchanged) ─────────────────────────────────
        ── Mobile:  2 rows
             Row 1: [TrendingUp + Title]
             Row 2: [Description · items-center]  ──→  [Tabs + ℹ]
    ─────────────────────────────────────────────────────────────────────── */}

                {/* Row 1 (mobile) / Left side (desktop): Title */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">

                  {/* Title block */}
                  <div className="min-w-0">
                    <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-primary shrink-0" />
                      Price Trend Analysis (Lowest Price)
                    </CardTitle>

                    {/* Row 2 on mobile: description + right-side controls */}
                    <div className="flex items-center justify-between mt-1 sm:mt-0 gap-2">

                      {/* Description — always visible */}
                      <CardDescription className="text-xs sm:text-sm">
                        Lowest price across 30 days
                      </CardDescription>

                      {/* Controls — mobile only (shown in row 2 right side) */}
                      <div className="flex items-center gap-1.5 sm:hidden shrink-0">
                        <Tabs value={chartTimeRange} onValueChange={setChartTimeRange}>
                          <TabsList className="h-7">
                            <TabsTrigger value="7d" className="text-xs px-2">7D</TabsTrigger>
                            <TabsTrigger value="14d" className="text-xs px-2">14D</TabsTrigger>
                            <TabsTrigger value="30d" className="text-xs px-2">30D</TabsTrigger>
                          </TabsList>
                        </Tabs>
                        <FormulaReveal {...priceChartFormula}>
                          <Button variant="surface" size="icon" className="h-7 w-7">
                            <Info className="h-3.5 w-3.5" />
                          </Button>
                        </FormulaReveal>
                      </div>
                    </div>
                  </div>

                  {/* Controls — desktop only (right side of single row) */}
                  <div className="hidden sm:flex items-center gap-2 shrink-0">
                    <Tabs value={chartTimeRange} onValueChange={setChartTimeRange}>
                      <TabsList className="h-8">
                        <TabsTrigger value="7d" className="text-xs px-2">7D</TabsTrigger>
                        <TabsTrigger value="14d" className="text-xs px-2">14D</TabsTrigger>
                        <TabsTrigger value="30d" className="text-xs px-2">30D</TabsTrigger>
                      </TabsList>
                    </Tabs>
                    <FormulaReveal {...priceChartFormula}>
                      <Button variant="surface" size="sm" className="h-7 gap-2 ml-2">
                        <Info className="h-3.5 w-3.5" />
                      </Button>
                    </FormulaReveal>
                  </div>

                </div>
              </CardHeader>

              <CardContent>
                {chartData?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={320}>
                    <ComposedChart data={chartData}>
                      <defs>
                        <linearGradient id="yourHotelGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" opacity={0.3} />
                      <XAxis
                        dataKey="date"
                        className="text-xs"
                        interval={chartTimeRange == "7d" ? "preserveStartEnd" : "preserveStart"}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                        stroke="hsl(var(--border))"
                      />
                      <YAxis
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                        tickFormatter={(v) => formatCurrency(v, activeCompset?.currency)}
                        stroke="hsl(var(--border))"
                      />
                      <Tooltip content={<PriceTooltip currency={activeCompset?.currency} />} />
                      <Legend wrapperStyle={{ paddingTop: '20px' }} />
                      <Area
                        type="monotone"
                        dataKey="yourHotel"
                        name="Your Hotel"
                        stroke="#3b82f6"
                        strokeWidth={3}
                        fill="url(#yourHotelGradient)"
                        dot={{ fill: '#3b82f6', r: 4 }}
                        activeDot={{ r: 6, fill: '#3b82f6' }}
                      />
                      <Line
                        type="monotone"
                        dataKey="marketAvg"
                        name="Market Avg"
                        stroke="#94a3b8"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[320px] text-muted-foreground">
                    No price trend data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Room Availability */}
            <Card className="border-2 border-primary/10 hover:shadow-xl transition-all">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-primary" />
                      Room Status
                    </CardTitle>
                    <CardDescription>Current availability</CardDescription>
                  </div>
                  <FormulaReveal {...roomAvailabilityFormula}>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Info className="h-4 w-4 text-muted-foreground hover:text-primary" />
                    </Button>
                  </FormulaReveal>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {roomData?.length > 0 ? roomData?.map((room, index) => {
                    const occupancy = room?.total > 0 ? Math.round((room?.available / room?.total) * 100) : 0;
                    const isYourHotel = room?.is_your_hotel;
                    return (
                      <div key={index} className="space-y-2.5">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2 flex-1">
                            <TooltipProvider>
                              <RadixToolTip>
                                <TooltipTrigger asChild>
                                  <span className={`font-medium ${isYourHotel ? 'text-primary' : 'text-foreground'}`}>
                                    {room?.hotel?.length > 25
                                      ? String(room?.hotel).slice(0, 25).concat("...")
                                      : room?.hotel || `Hotel ${index + 1}`}
                                  </span>
                                </TooltipTrigger>

                                <TooltipContent
                                  className="px-2 py-1 text-sm bg-black text-white rounded shadow"
                                  side="top"
                                  align="center"
                                >
                                  {room?.hotel || `Hotel ${index + 1}`}
                                </TooltipContent>
                              </RadixToolTip>
                            </TooltipProvider>
                            {isYourHotel && <Badge variant="default" className="text-xs">You</Badge>}
                          </div>
                          <span className="text-muted-foreground text-xs">
                            {room?.available}/{room?.total} available
                          </span>
                        </div>
                        <div className="relative flex justify-between gap-2 items-center">
                          <Progress
                            value={occupancy}
                            className={`h-2.5 ${isYourHotel ? 'bg-primary/20' : 'bg-muted'}`}
                          />
                          <span className="text-xs font-medium text-foreground">
                            {occupancy}%
                          </span>
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="text-center text-muted-foreground py-8 text-sm">
                      No availability data
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* AI Insight - Modern gradient card */}
          {
            canAccessAiInsights() && (
              <Card
                className="relative overflow-hidden border-2 border-primary/20 hover:shadow-2xl transition-all"
                data-testid="ai-market-analysis-card"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5" />

                <CardContent className="relative p-4 sm:p-6">

                  {/* ── Header ──────────────────────────────────────────────────────────────
        Mobile:  2 rows
          Row 1: [Icon + Title + Badge]
          Row 2:             justify-end → [Generate/Refresh] [x/x left] [ℹ]
        Desktop: 1 row
          [Icon + Title + Badge] ─── flex-1 ─── [Generate/Refresh] [x/x left] [ℹ]
    ─────────────────────────────────────────────────────────────────────────── */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">

                    {/* Left: Icon + Title + Badge */}
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <div className="p-2.5 sm:p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg shrink-0">
                        {aiMarketLoading ? (
                          <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 text-white animate-spin" />
                        ) : (
                          <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap min-w-0">
                        <h3 className="font-semibold text-foreground text-base sm:text-lg leading-tight">
                          AI Market Analysis
                        </h3>
                        {isMarketAIGenerated && (
                          <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-600 border-purple-500/30">
                            Prowen AI
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Right: Actions — justify-end on mobile, inline on desktop */}
                    <div className="flex items-center justify-end gap-2 shrink-0">

                      {/* Generate button */}
                      {(!isEnabled && !hasAiInsightData) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 h-8 shrink-0"
                          onClick={enableAiInsight}
                        >
                          <span className="text-xs">Generate AI Insights</span>
                        </Button>
                      )}

                      {/* Refresh button */}
                      {(calculateAiInsightRefreshCount("market") > 0 && (isEnabled || hasAiInsightData))
                        ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1.5 h-8 shrink-0 px-2 sm:px-3"
                            onClick={() => {
                              enableAiInsight();
                              refetchMarketAI("regenerate");
                            }}
                            disabled={aiMarketLoading}
                            title="Refresh AI Analysis"
                          >
                            <RefreshCw className={`h-4 w-4 shrink-0 ${aiMarketLoading ? 'animate-spin' : ''}`} />
                            <span className="text-xs sm:hidden">Refresh</span>
                          </Button>
                        )
                        : null
                      }

                      {/* Usage counter */}
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {`${calculateAiInsightRefreshCount("market")} / ${maxAiRefreshPerSection} left`}
                      </span>

                      {/* Info */}
                      <FormulaReveal {...HOTEL_FORMULAS.MARKET_INSIGHT_AI}>
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                          <Info className="h-4 w-4 text-muted-foreground hover:text-primary" />
                        </Button>
                      </FormulaReveal>
                    </div>
                  </div>

                  {/* Body */}
                  <p className="text-sm font-medium leading-relaxed whitespace-pre-wrap break-words">
                    {aiMarketLoading
                      ? 'Analyzing market position with Prowen AI...'
                      : aiMarketInsight
                        ? aiMarketInsight
                        : hasAiInsightData
                          ? hasAiInsightData?.insight
                          : null}
                  </p>

                </CardContent>
              </Card>
            )
          }
        </TabsContent>

        {/* Tab 2: Demand & Events */}
        <TabsContent value="demand-events" className="space-y-6">
          {/* Demand Calendar */}
          <DemandCalendar />

          {/* Upcoming Events */}
          {/* <Card className="border-2 border-primary/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Upcoming Events Impacting Demand
                  </CardTitle>
                  <CardDescription>Local events affecting hotel demand in your area</CardDescription>
                </div>
                <FormulaReveal
                  title="Events Impact Analysis"
                  type="data"
                  formula="Demand_Impact = Expected_Attendees × Location_Factor × Event_Type_Weight"
                  description="Calculates expected demand increase based on local events."
                  dataSource="Events API & Historical Patterns"
                >
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <Eye className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                  </Button>
                </FormulaReveal>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {displayEvents.length > 0 ? displayEvents.map((event, idx) => {
                  const impactColors = {
                    'high': { border: 'emerald-500', bg: 'emerald-500', text: 'emerald-500' },
                    'medium': { border: 'blue-500', bg: 'blue-500', text: 'blue-500' },
                    'low': { border: 'amber-500', bg: 'amber-500', text: 'amber-500' }
                  };
                  const colors = impactColors[event.impact_category] || impactColors['low'];

                  return (
                    <div key={idx} className={`p-4 rounded-lg border border-${colors.border}/20 bg-${colors.bg}/5`}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <p className="font-medium">{event.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(event.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {event.end_date !== event.start_date && ` - ${new Date(event.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                          </p>
                        </div>
                        <Badge className={event.impact_category === 'high' ? 'bg-emerald-500' : event.impact_category === 'medium' ? 'bg-blue-500 text-white' : ''} variant={event.impact_category === 'low' ? 'outline' : 'default'}>
                          {event.impact_category === 'high' ? 'High Impact' : event.impact_category === 'medium' ? 'Medium Impact' : 'Low Impact'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{event.description}</p>
                      <div className="flex items-center gap-2">
                        <TrendingUp className={`h-4 w-4 text-${colors.text}`} />
                        <span className={`text-sm font-medium text-${colors.text}`}>
                          +{Math.round(event.demand_impact)}% demand expected
                        </span>
                      </div>
                    </div>
                  );
                }) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No upcoming events found</p>
                    <p className="text-sm mt-2">Events will appear here when detected near your location</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card> */}
        </TabsContent>
      </Tabs>
    </div>
  );
}
